<script>
import { debounce } from 'lodash'
import baseMixin from '@/mixins/baseMixin'
import dateMixin from '@/mixins/dateMixin'
import VuetifyTextClass from '@/shared/constants/core/VuetifyTextClass'
import VuetifyColorClass from '@/shared/constants/core/VuetifyColourClass'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import { DurationUnits } from '@/shared/constants/date/DurationUnits'

// Sets the max date that can be selected
const MAX_DATE_VALUE = 90
const MAX_DATE_UNIT = DurationUnits.DAY

export default {
  name: 'ExtendBookingDatePicker',
  mixins: [baseMixin, dateMixin],
  model: {
    prop: 'extendedEndDate',
    event: 'input',
  },
  props: {
    booking: {
      type: Object,
      required: true,
    },
    extendedEndDate: {
      type: String,
      default: '',
    },
    minAllowedDate: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    ...VuetifyTextClass,
    ...VuetifyColorClass,
    showDatePicker: false,
    isValid: true,
    internalDate: '',
  }),
  computed: {
    bookingDescription() {
      const days = this.booking?.blockDetails?.bookingsCount || 1
      const prefix = this.$t('booking.extend.bodyTextPrefix')
      const suffix = this.$t('booking.extend.bodyTextSuffix')

      return `${prefix} ${days} ${this.$tc(
        'booking.extend.days',
        days
      )} ${suffix} ${this.friendlyEndDate}.`
    },
    maxAllowedDate() {
      return this.formatDate(
        this.dateAdd(this.minAllowedDate, MAX_DATE_VALUE, MAX_DATE_UNIT),
        DateFormatToken.YYYYMMDD
      )
    },
    endDate() {
      return (
        this.booking?.blockDetails?.endDateLocal ??
        this.booking.endDateTimeLocal
      )
    },
    friendlyEndDate() {
      return this.endDate ? this.formatDateToFriendlyString(this.endDate) : ''
    },
  },
  methods: {
    openDatePicker() {
      this.internalDate =
        this.extendedEndDate && this.isValid
          ? this.extendedEndDate
          : this.minAllowedDate
      this.showDatePicker = !this.showDatePicker
    },
    emitSetDateEvent(value, isValid) {
      this.$emit('input', value)
      this.$emit('update', { value, isValid: isValid })
    },
    setDate: debounce(function(value) {
      this.isValid = this.validateDate()
      this.emitSetDateEvent(value, this.isValid)
    }, 150),
    setDatePickerDate() {
      this.emitSetDateEvent(this.internalDate, true)
      this.showDatePicker = false
    },
    validateDate() {
      return this.$refs.form.validate()
    },
    validDateRule(value) {
      return (!!value && this.isValidDate(value)) || this.$t('input.validDate')
    },
    futureDatesOnlyRule(selectedDate) {
      const today = this.startOf(this.getTodaysDate(), DurationUnits.DAY)
      return (
        this.isSameOrAfter(selectedDate, today) ||
        this.$t('input.dateInPastValidationError')
      )
    },
    minDateRule(selectedDate) {
      return (
        this.isSameOrAfter(selectedDate, this.minAllowedDate) ||
        this.$t('input.minDateValidationError', [this.minAllowedDate])
      )
    },
    maxDateRule(selectedDate) {
      if (!selectedDate) return true
      return (
        this.isSameOrBefore(selectedDate, this.maxAllowedDate) ||
        this.$t('booking.extend.maxExtensionValidationError', [
          this.humanReadableDuration(
            this.maxAllowedDate,
            this.minAllowedDate,
            MAX_DATE_UNIT
          ),
          this.maxAllowedDate,
        ])
      )
    },
  },
}
</script>

<template>
  <div>
    <p :class="smallBody">{{ bookingDescription }}</p>
    <div :class="[isMediumOrHigher ? 'd-flex align-center' : '']">
      <p :class="[isMediumOrHigher ? 'mr-4 mb-6' : '', smallBody]">{{
        $t('booking.extend.inputLabel')
      }}</p>

      <v-form ref="form" v-model="isValid">
        <v-menu
          ref="menu"
          v-model="showDatePicker"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          rounded="lg"
        >
          <template v-slot:activator="{ attrs }">
            <v-text-field
              :value="extendedEndDate"
              :label="$t('booking.extend.endDate')"
              type="date"
              append-icon="mdi-calendar"
              outlined
              dense
              class="end-date-field"
              :class="[smallBody, { 'end-date-field--lg': isMediumOrHigher }]"
              :rules="[
                validDateRule,
                futureDatesOnlyRule,
                minDateRule,
                maxDateRule,
              ]"
              v-bind="attrs"
              @input="setDate"
              @click:append="openDatePicker"
            />
          </template>
          <v-date-picker
            v-model="internalDate"
            :color="colorClass.blue"
            no-title
            :first-day-of-week="firstDayOfWeek"
            :min="minAllowedDate"
            :max="maxAllowedDate"
            @input="setDatePickerDate"
          ></v-date-picker>
        </v-menu>
      </v-form>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/_colors.scss';

.end-date-field {
  border-radius: 8px;

  &.end-date-field--lg {
    max-width: 300px;
  }

  // Removes html5 calendar picker so we can use our own
  ::v-deep input[type='date']::-webkit-inner-spin-button,
  ::v-deep input[type='date']::-webkit-calendar-picker-indicator {
    display: none;
    -webkit-appearance: none;
  }
}
</style>
